import { API, graphqlOperation } from "aws-amplify";
import { GraphQLResult } from "@aws-amplify/api-graphql";
import { ConversationsByProjectIdAndCreatedAtQuery } from "../API";
import { conversationsByProjectIdAndCreatedAt } from "../graphql/queries";
import { useInfiniteQuery } from "@tanstack/react-query";

interface ConversationsPage {
  conversations: any[]; 
  nextToken: string | null;
}

async function fetchConversations({
  projectId,
  limit = 25,
  nextToken = null,
}: {
  projectId: string;
  limit?: number;
  nextToken?: string | null;
}): Promise<ConversationsPage> {
  const query: any = {
    projectId: projectId,
    sortDirection: "DESC",
    limit: limit,
    nextToken: nextToken,
  };

  try {
    const result = (await API.graphql(
      graphqlOperation(conversationsByProjectIdAndCreatedAt, query)
    )) as GraphQLResult<ConversationsByProjectIdAndCreatedAtQuery>;

    const conversations = result.data?.conversationsByProjectIdAndCreatedAt?.items || [];
    const newNextToken = result.data?.conversationsByProjectIdAndCreatedAt?.nextToken;

    return {
      conversations: conversations,
      nextToken: newNextToken || null,
    };
  } catch (error) {
    console.error('Error fetching conversations:', error);
    throw error;
  }
}

// Updated helper function with better pagination handling
async function fetchConversationsWithCalls({
  projectId,
  limit = 25,
  nextToken = null,
  accumulator = [] as any[],
  fetchedCount = 0,
}: {
  projectId: string;
  limit?: number;
  nextToken?: string | null;
  accumulator?: any[];
  fetchedCount?: number;
}): Promise<ConversationsPage> {
  // Fetch more items to account for filtering
  const fetchLimit = Math.min(50, limit * 3);
  
  const page = await fetchConversations({
    projectId,
    limit: fetchLimit,
    nextToken,
  });
  
  // Filter out conversations without calls
  const filtered = page.conversations.filter(conv => conv.calls?.items?.length > 0);
  const accumulated = accumulator.concat(filtered);
  
  // If we have enough conversations or no more pages, return results
  if (accumulated.length >= limit || !page.nextToken) {
    return {
      conversations: accumulated.slice(0, limit),
      nextToken: accumulated.length >= limit && page.nextToken ? page.nextToken : null,
    };
  }
  
  // Continue fetching if we need more conversations
  return await fetchConversationsWithCalls({
    projectId,
    limit,
    nextToken: page.nextToken,
    accumulator: accumulated,
    fetchedCount: fetchedCount + page.conversations.length,
  });
}

export const useConversations = (projectId: string, limit = 25) => {
  return useInfiniteQuery<ConversationsPage, Error>({
    queryKey: ["conversations", projectId, limit],
    queryFn: async ({ pageParam = null }) => 
      await fetchConversationsWithCalls({
        projectId,
        limit,
        nextToken: pageParam as string | null,
      }),
    initialPageParam: null,
    getNextPageParam: (lastPage: ConversationsPage) =>
      lastPage.nextToken || undefined,
    staleTime: 30000, // 30 seconds
    refetchOnWindowFocus: true,
  });
};